import showToast from "hooks/useCustomToast";
import { ChatRequest, ChatResponse, ChatClientType, chatClientTypeToJSON } from "build-ts/chat_session";
import { DarwinHeaderList, SortBy, sortByToJSON, SortOrder, sortOrderToJSON } from "build-ts/common";

/**
 * Continue a chat session.
 * This is used to continue a chat session with a new message.
 *
 * @param sessionId - The session ID
 * @param message - The message to continue the chat session
 * @param autoSave - Whether to save message in the database automatically
 * @returns The chat response
 */
export const continueSession = async (
  sessionId: string,
  message: string,
  autoSave: boolean = true,
  userId: string,
): Promise<ChatResponse> => {
  try {
    const chatRequest = ChatRequest.create({
      session_header: { id: sessionId, author_id: userId },
      message: message,
      is_save: autoSave,
    });
    const response = await fetch(`${process.env.REACT_APP_CHAT_MESSAGE}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(ChatRequest.toJSON(chatRequest)),
    });

    if (!response.ok) {
      showToast("Failed to continue session", "", "error");
    }
    const data = await response.json();
    return ChatResponse.fromJSON(data);
  } catch (error: any) {
    showToast("Error continuing session", error?.message || "Unknown error occurred", "error");
    throw error;
  }
};

/**
 * Save a chat session.
 * This is used to save a chat session to the database.
 *
 * @param sessionId - The session ID
 * @param title - Optional title to set before saving
 * @param description - Optional description to set before saving
 * @returns The response from the server. If the request is successful, the response will be a 200 status code.
 * @throws {Error} Throws an error if the request fails
 */
export const saveSession = async (sessionId: string, title?: string, description?: string): Promise<Response> => {
  try {
    let url = `${process.env.REACT_APP_SAVE_CHAT_SESSION}?session_id=${encodeURIComponent(sessionId)}`;
    if (title) {
      url += `&title=${encodeURIComponent(title)}`;
    }
    if (description) {
      url += `&description=${encodeURIComponent(description)}`;
    }
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      showToast("Failed to save chat session", "", "error");
    }
    showToast("Successfully saved chat session", "", "success");
    return response;
  } catch (error: any) {
    showToast("Error saving chat session", error?.message || "Unknown error occurred", "error");
    throw error;
  }
};

/**
 * Initialize a new chat session.
 * This is used to initialize a chat session
 *
 * @param request - The chat request
 * @returns The chat response
 */
export const initializeChatSession = async (request: ChatRequest): Promise<ChatResponse> => {
  try {
    const chatResponse = await fetch(`${process.env.REACT_APP_CHAT_MESSAGE}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(ChatRequest.toJSON(request))
    });
    if (!chatResponse.ok) {
      showToast("Failed to initialize chat session", "", "error");
    }
    const data = await chatResponse.json();
    return ChatResponse.fromJSON(data);
  } catch (error: any) {
    showToast("Error initializing chat session", error?.message || "Unknown error occurred", "error");
    throw error;
  }
};

/**
 * Fetch a chat session.
 * This is used to fetch all messages in a chat session.
 *
 * @param sessionId - The session ID
 * @param initializeTransaction - Whether to initialize the transaction in the stateful store for continuation
 * @returns The chat messages
 * @throws {Error} Throws an error if the request fails
 */
export const fetchChatSession = async (sessionId: string, initializeTransaction: boolean = false, includeMessages: boolean = true): Promise<ChatResponse> => {
  try {
    const url = `${process.env.REACT_APP_CHAT_SESSION}/${encodeURIComponent(sessionId)}?initialize_transaction=${initializeTransaction}&include_messages=${includeMessages}`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });
    if (!response.ok) {
      showToast("Failed to fetch chat session", "", "error");
    }
    const data = await response.json();
    return ChatResponse.fromJSON(data);
  } catch (error: any) {
    showToast("Error fetching chat session", error?.message || "Unknown error occurred", "error");
    throw error;
  }
};


/**
 * Fetch the list of all chat sessions.
 *
 * @param {ChatClientType[]} clientType - Filter by client type (required).
 * @param {number} limit - Maximum number of sessions to return (1-100).
 * @param {number} offset - Number of sessions to skip for pagination.
 * @param {string} titleSearch - Optional search string to filter by title.
 * @param {string} agentId - Optional filter by agent ID.
 * @param {Header[]} associatedEntityKey - Optional filter by associated entity headers.
 * @param {SortBy} sortBy - Field to sort by (default: LAST_MODIFIED_DATE).
 * @param {SortOrder} sortOrder - Sort order (default: DESC).
 * @returns {Promise<DarwinHeaderList>} A promise that resolves to DarwinHeaderList containing headers and total count.
 * @throws {Error} Throws an error if the request fails
 */
export const fetchListOfChatSessions = async (
  clientType: ChatClientType[] = [ChatClientType.FRIDA],
  limit: number = 50,
  offset: number = 0,
  titleSearch?: string,
  agentId?: string,
  associatedEntityKey?: string[],
  sortBy: SortBy = SortBy.LAST_MODIFIED_DATE,
  sortOrder: SortOrder = SortOrder.DESC,
  userId?: string,
): Promise<DarwinHeaderList> => {
  try {
    const queryParams = new URLSearchParams({
      client_type: JSON.stringify(clientType.map((clientType: ChatClientType) => chatClientTypeToJSON(clientType))),
      limit: limit.toString(),
      offset: offset.toString(),
      sort_by: sortByToJSON(sortBy),
      sort_order: sortOrderToJSON(sortOrder),
    });

    // Add optional parameters
    if (titleSearch && titleSearch.trim()) {
      queryParams.append("title_search", titleSearch.trim());
    }

    if (agentId && agentId.trim()) {
      queryParams.append("agent_id", agentId.trim());
    }

    if (associatedEntityKey) {
      queryParams.append("associated_entity_key", JSON.stringify(associatedEntityKey));
    }
    if (userId) {
      queryParams.append("user_id", userId);
    }

    const response = await fetch(`${process.env.REACT_APP_LIST_CHAT_SESSIONS}?${queryParams}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      showToast("Failed to fetch list of chat sessions", "", "error");
      // Return empty DarwinHeaderList on error
      return DarwinHeaderList.fromJSON({ headers: [], total: 0 });
    }

    const data = await response.json();
    return DarwinHeaderList.fromJSON(data);

  } catch (error: any) {
    showToast("Error fetching list of chat sessions", error?.message || "Unknown error occurred", "error");
    throw error;
  }
};


/**
 * Delete a chat session.
 *
 * @param sessionId - The session ID
 * @throws {Error} Throws an error if the request fails
 */
export const deleteChatSession = async (sessionId: string) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_CHAT_SESSION}/${sessionId}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!response.ok) {
      showToast("Failed to delete chat session", "", "error");
    }
    showToast("Chat session deleted successfully", "", "success");
    return response;
  } catch (error: any) {
    showToast("Error deleting chat session", error?.message || "Unknown error occurred", "error");
    throw error;
  }
};

/**
 * Update chat session headers (title and/or visibility) in Darwin.
 *
 * @param {string[]} sessionIds - Array of chat session IDs to update (required, cannot be empty).
 * @param {string} [title] - New title for the session (optional, requires exactly 1 session ID).
 * @param {string} [description] - Chat session description (optional, requires exactly 1 session ID).
 * @param {boolean} [isHidden] - Whether the sessions should be hidden (optional, applies to all session IDs).
 * @returns {Promise<any | null>} A promise that resolves to the update result from the server.
 * @throws {Error} Throws an error if there is an issue updating chat session headers or if the request fails.
 */
export const updateChatSessionHeaders = async (
  sessionIds: string[],
  title?: string,
  description?: string,
  isHidden?: boolean,
): Promise<boolean> => {
  try {
    const queryParams = new URLSearchParams({
      session_ids: JSON.stringify(sessionIds),
    });

    if (title !== undefined) {
      queryParams.append("title", title);
    }

    if (description !== undefined) {
      queryParams.append("description", description);
    }

    if (isHidden !== undefined) {
      queryParams.append("is_hidden", isHidden.toString());
    }

    const response = await fetch(
      `${process.env.REACT_APP_UPDATE_CHAT_HEADERS}?${queryParams}`,
      {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      showToast("Failed to update chat session headers", "", "error");
      return false;
    }

    // Backend returns 204 No Content on success
    return true;
  } catch (error) {
    showToast("Failed to update chat session headers", "", "error");
    return false;
  }
};

export interface DuplicateSessionParams {
  originalSessionId: string;
  currentUserId: string;
  clientType: string;
  save?: boolean;
  initTransaction?: boolean;
}

/**
 * Duplicate a chat session into a new independent session.
 */
export const duplicateChatSession = async (
  params: DuplicateSessionParams,
): Promise<ChatResponse | null> => {
  const { originalSessionId, currentUserId, clientType, save = true, initTransaction = false } = params;
  const query = new URLSearchParams({
    original_session_id: originalSessionId,
    current_user_id: currentUserId,
    client_type: clientType,
    save: String(save),
    init_transaction: String(initTransaction),
  });
  try {
    const response = await fetch(`${process.env.REACT_APP_DUPLICATE_CHAT_SESSION}?${query}`, {
      method: "POST",
      credentials: "include",
    });

    if (!response.ok) {
      showToast("Failed to duplicate session", "", "error");
      throw new Error("Failed to duplicate session");
    }

    const data = await response.json();
    return ChatResponse.fromJSON(data);
  } catch (error: any) {
    showToast("Error duplicating session", error?.message || "Unknown error occurred", "error");
    return null;
  }
};
